import { HardHat } from 'lucide-react';
import { Link } from 'react-router-dom';

import { itemPorCaminho } from '@/nucleo/navegacao/mapa';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';

/** O que cada fase entrega, para a tela dizer o que vem — e não só "aguarde". */
const FASES: Record<number, string> = {
  2: 'Credenciamento',
  3: 'Frota e contratos',
  4: 'Operação',
  5: 'Apuração e financeiro',
  6: 'Fiscalização',
  7: 'Portal do transportador e da rede',
  8: 'Administração e governança',
};

interface ModuloEmConstrucaoProps {
  /** Caminho do item no mapa de navegação. Dele saem título, fase e resumo. */
  caminho: string;
  /** Para onde voltar. A raiz da área em que o módulo vive. */
  voltarPara: string;
}

/**
 * Tela de módulo ainda não construído.
 *
 * docs/PLANO-DE-FASES.md, fase 1: "itens de menu ainda não construídos levam a um
 * estado 'módulo em construção' — nunca a uma tela quebrada".
 *
 * Ela diz três coisas, porque as três evitam que a pessoa fique sem saber o que
 * fazer (CLAUDE.md, "Regras de interface", item 8): o que este módulo vai fazer,
 * em que fase ele entra, e por onde voltar. Nada de "em breve" sem contexto.
 *
 * Não é erro: por isso não usa `ErrorState`. Ninguém errou ao chegar aqui — o
 * item está no menu de propósito, para o sistema mostrar para onde vai.
 */
export function ModuloEmConstrucao({ caminho, voltarPara }: ModuloEmConstrucaoProps) {
  const item = itemPorCaminho(caminho);
  const titulo = item?.titulo ?? 'Módulo em construção';
  const fase = item?.fase;
  const nomeDaFase = fase ? FASES[fase] : undefined;

  return (
    <div className="flex flex-1 items-center justify-center py-8">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <HardHat className="h-6 w-6" aria-hidden="true" />
          </div>
          <CardTitle>{titulo}</CardTitle>
          <CardDescription>
            {item?.resumo ?? 'Este módulo entra numa próxima fase da construção.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {nomeDaFase ? (
              <>
                Entra na <strong className="font-semibold text-foreground">fase {fase}</strong> do
                plano de construção — <span className="text-foreground">{nomeDaFase}</span>. Cada
                módulo é entregue completo: telas, regras, filtros, escopo por perfil e validação
                com a Secretaria.
              </>
            ) : (
              <>
                Cada módulo é entregue completo: telas, regras, filtros, escopo por perfil e
                validação com a Secretaria.
              </>
            )}
          </p>

          <Button asChild variant="outline">
            <Link to={voltarPara}>Voltar</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default ModuloEmConstrucao;
